import { useEffect, useState } from 'react';
import customFetch from '../../../api/customFetch';

const SECTION_LABELS = {
  'part-a': "א' - העובד וסביבת העבודה",
  'part-b': "ב' - עבודת צוות ושייכות",
  'part-c': "ג' - נראות והערכה",
  'part-d': "ד' - המנהל הישיר",
  'part-e': "ה' - ההנהלה הבכירה",
  'part-f': "ו' - פיתוח מקצועי",
  'part-g': "ז' - איזון בית-עבודה",
  'part-h': "ח' - נאמנות ושביעות רצון",
};

const scoreColor = (avg) => {
  if (!avg) return '#999';
  if (avg >= 4) return '#4caf50';
  if (avg >= 3) return '#ff9800';
  return '#f44336';
};

const ScoreBar = ({ value, max = 5 }) => {
  const pct = Math.min(100, ((value || 0) / max) * 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, background: '#e0e0e0', borderRadius: 4, height: 12, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: scoreColor(value), borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      <span style={{ minWidth: 36, fontWeight: 600, color: scoreColor(value) }}>
        {value ? value.toFixed(2) : '—'}
      </span>
    </div>
  );
};

export default function SurveyAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    load();
  }, [year]);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await customFetch.get(`/survey/analytics/full?year=${year}`);
      setData(res.data);
    } catch (e) {
      setError('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = await customFetch.get(`/survey/analytics/export?year=${year}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey-${year}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('שגיאה בייצוא הקובץ');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className='admin-page'>
        <div className='loading-container'>
          <div className='loading-spinner' />
          <span>טוען נתוני סקרים...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='admin-page'>
        <div className='admin-alert error'>{error}</div>
        <button className='admin-btn primary' onClick={load}>נסה שוב</button>
      </div>
    );
  }

  const { total, overallAvg, sectionAverages, bySector, byBranch, availableYears } = data || {};

  return (
    <div className='admin-page'>
      {/* כותרת */}
      <div className='page-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <h1 className='page-title'>
          <span className='page-title-icon'>📋</span>
          סקרי אקלים ארגוני
        </h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--admin-border)', background: 'var(--admin-card)', color: 'var(--admin-text)', fontSize: '0.95rem', cursor: 'pointer' }}
          >
            {(availableYears || [year]).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button className='admin-btn primary' onClick={load}>🔄 רענן</button>
          <button
            className='admin-btn secondary'
            onClick={handleExport}
            disabled={exporting || !total}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {exporting ? '⏳ מייצא...' : '📥 הורד Excel'}
          </button>
        </div>
      </div>

      {/* כרטיסי סיכום */}
      <div className='stats-grid'>
        <div className='stat-card'>
          <div className='stat-icon blue'>📝</div>
          <div className='stat-content'>
            <div className='stat-value'>{total ?? 0}</div>
            <div className='stat-label'>סקרים הוגשו</div>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon green'>⭐</div>
          <div className='stat-content'>
            <div className='stat-value' style={{ color: scoreColor(overallAvg) }}>
              {overallAvg ? overallAvg.toFixed(2) : '—'}
            </div>
            <div className='stat-label'>ממוצע כללי (1-5)</div>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon purple'>🏢</div>
          <div className='stat-content'>
            <div className='stat-value'>{bySector?.length ?? 0}</div>
            <div className='stat-label'>סקטורים</div>
          </div>
        </div>
        <div className='stat-card'>
          <div className='stat-icon orange'>🏥</div>
          <div className='stat-content'>
            <div className='stat-value'>{byBranch?.length ?? 0}</div>
            <div className='stat-label'>סניפים</div>
          </div>
        </div>
      </div>

      {total === 0 && (
        <div className='admin-card'>
          <p style={{ color: 'var(--admin-text-muted)', textAlign: 'center', padding: 40 }}>
            אין סקרים שהוגשו לשנת {year}
          </p>
        </div>
      )}

      {total > 0 && (
        <>
          {/* ממוצע לפי חלק */}
          <div className='admin-card'>
            <div className='card-header'>
              <h3 className='card-title'>📊 ממוצע לפי חלק בסקר</h3>
            </div>
            <div style={{ padding: '0 16px 16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
                {(sectionAverages || []).map((s) => (
                  <div key={s.sectionId} style={{ display: 'grid', gridTemplateColumns: '220px 1fr 60px', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <span style={{ color: 'var(--admin-text)', fontSize: '0.9rem' }}>
                      {SECTION_LABELS[s.sectionId] || s.sectionId}
                    </span>
                    <ScoreBar value={s.avg} />
                    <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.8rem', textAlign: 'center' }}>
                      {s.count} ענו
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ממוצע לפי סקטור */}
          {bySector?.length > 0 && (
            <div className='admin-card'>
              <div className='card-header'>
                <h3 className='card-title'>🏢 ממוצע לפי סקטור</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--admin-text)', fontSize: '0.87rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap' }}>סקטור</th>
                      {Object.entries(SECTION_LABELS).map(([id, label]) => (
                        <th key={id} style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid rgba(255,255,255,0.1)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          {id.replace('part-', "חלק '").toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bySector.map((row) => {
                      const secMap = Object.fromEntries((row.sections || []).map((s) => [s.sectionId, s.avg]));
                      return (
                        <tr key={row.sector} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600 }}>{row.sector}</td>
                          {Object.keys(SECTION_LABELS).map((id) => (
                            <td key={id} style={{ padding: '10px 8px', textAlign: 'center', color: scoreColor(secMap[id]) }}>
                              {secMap[id] ? secMap[id].toFixed(2) : '—'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ממוצע לפי סניף */}
          {byBranch?.length > 0 && (
            <div className='admin-card'>
              <div className='card-header'>
                <h3 className='card-title'>🏥 ממוצע לפי סניף</h3>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: 'var(--admin-text)', fontSize: '0.87rem' }}>
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                      <th style={{ padding: '10px 14px', textAlign: 'right', borderBottom: '2px solid rgba(255,255,255,0.1)' }}>סניף</th>
                      {Object.entries(SECTION_LABELS).map(([id]) => (
                        <th key={id} style={{ padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid rgba(255,255,255,0.1)', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                          {id.replace('part-', "חלק '").toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {byBranch.map((row) => {
                      const secMap = Object.fromEntries((row.sections || []).map((s) => [s.sectionId, s.avg]));
                      return (
                        <tr key={row.branch} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                          <td style={{ padding: '10px 14px', fontWeight: 600 }}>{row.branch}</td>
                          {Object.keys(SECTION_LABELS).map((id) => (
                            <td key={id} style={{ padding: '10px 8px', textAlign: 'center', color: scoreColor(secMap[id]) }}>
                              {secMap[id] ? secMap[id].toFixed(2) : '—'}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* מקרא צבעים */}
          <div className='admin-card' style={{ padding: 16 }}>
            <div className='card-header'>
              <h3 className='card-title'>📌 מקרא ציונים</h3>
            </div>
            <div style={{ display: 'flex', gap: 24, padding: '8px 0', flexWrap: 'wrap' }}>
              {[
                { label: '4.0 ומעלה — גבוה', color: '#4caf50' },
                { label: '3.0–3.9 — בינוני', color: '#ff9800' },
                { label: 'מתחת ל-3.0 — נמוך', color: '#f44336' },
              ].map((item) => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: item.color }} />
                  <span style={{ color: 'var(--admin-text)', fontSize: '0.88rem' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
